/**
 * **Die Wohneinheiten eines Mieterstrom-Konzepts als Zusammensetzung** — im
 * Aufbau der Karte „Zusammensetzung des Verbrauchs" (HM-118, HM-120): ein
 * Balken über alle Wohnungen, ihre Herkunft zusammen, dann je Wohnung eine
 * Zeile mit Anteil und Herkunft.
 *
 * **Das Ganze sind die Wohnungen, nicht der Verbrauch der Anlage.** Der Anteil
 * einer Wohnung ist ihr Teil an der Summe der Wohnungen; wie sich diese Summe
 * zu den weiteren Verbrauchern verhält, sagt die Zusammensetzung darüber.
 *
 * **Die Farben sind die Helligkeitsreihe des Diagramms** (`MAX_FADE`, ADR 0013
 * Nachtrag): dieselbe Wohnung, dieselbe Stufe wie im Stapel „Verbrauch". Ohne
 * Grenze — ab etwa sechs Wohnungen liest man über die Zeilen, nicht über den
 * Balken. **Fehlt einer Wohnung die Summe, gibt es keinen Balken**: Ein Ganzes
 * ohne einen Teil wäre kleiner und sähe trotzdem vollständig aus (Regel 8).
 *
 * Die Herkunft einer Wohnung sind ihr PV- und ihr Netzanteil
 * (`unit_pv_share`, `unit_grid_share`) — die API rechnet sie je Viertelstunde.
 * Die Herkunft aller Wohnungen ist deren Summe, und nur, wenn jede eine hat.
 */
import type { ApiComponents } from "@hausmeter/sdk";
import { MAX_FADE } from "./consumption-split.js";
import { lessCertainQuality } from "./energy-format.js";

type Subject = ApiComponents["schemas"]["SubjectEnergyTotals"];
type Total = ApiComponents["schemas"]["SiteEnergyTotal"];
type Origin = ApiComponents["schemas"]["EnergyOrigin"];

export interface UnitCompositionRow {
  readonly key: string;
  readonly siteAreaId: string | null;
  readonly label: string;
  readonly total: Total | undefined;
  /** Anteil an der Summe der Wohnungen, 0…1 — `null`, wenn eine Summe fehlt. */
  readonly share: number | null;
  /** Stufe in der Helligkeitsreihe des Verbrauchs — `null` ohne Balken. */
  readonly fade: number | null;
  readonly origin: Origin | undefined;
  /** Verbraucher hinter dem Zähler dieser Wohnung (HM-111) — Teil ihres Verbrauchs. */
  readonly consumers: readonly { readonly key: string; readonly label: string; readonly total: Total | undefined }[];
}

export interface UnitsComposition {
  readonly rows: readonly UnitCompositionRow[];
  /** Summe der Wohnungen in Wh — `null`, sobald einer die Summe fehlt. */
  readonly sum: number | null;
  readonly coverage: number;
  readonly quality: Total["quality"];
  readonly origin: Origin | undefined;
  readonly stackable: boolean;
}

const totalOf = (subject: Subject, key: string): Total | undefined =>
  subject.totals.find((total) => total.quantityKey === key);

function originOf(subject: Subject): Origin | undefined {
  const pv = totalOf(subject, "unit_pv_share");
  const grid = totalOf(subject, "unit_grid_share");
  if (pv === undefined || grid === undefined) return undefined;
  return {
    pv: pv.value,
    grid: grid.value,
    coverage: Math.min(pv.coverage, grid.coverage),
    quality: lessCertainQuality(pv.quality, grid.quality),
  };
}

export function unitsComposition(subjects: readonly Subject[]): UnitsComposition | null {
  const units = subjects.filter((subject) => subject.kind === "unit");
  if (units.length === 0) return null;
  const totals = units.map((unit) => totalOf(unit, "unit_consumption"));
  const values = totals.map((total) => total?.value ?? null);
  const known = values.every((value): value is number => value !== null);
  const sum = known ? values.reduce((a, b) => a + b, 0) : null;
  const stackable = sum !== null && sum > 0;
  const last = Math.max(1, units.length - 1);

  const rows = units.map((unit, index): UnitCompositionRow => {
    const total = totals[index];
    return {
      key: unit.subjectKey,
      siteAreaId: unit.siteAreaId,
      label: unit.label,
      total,
      share: stackable && total?.value != null ? total.value / sum : null,
      fade: stackable ? (units.length === 1 ? 0 : (index / last) * MAX_FADE) : null,
      origin: originOf(unit),
      consumers: subjects
        .filter(
          (s) => s.kind === "consumer" && s.siteAreaId !== null && s.siteAreaId === unit.siteAreaId,
        )
        .map((s) => ({ key: s.subjectKey, label: s.label, total: totalOf(s, "load_consumption") })),
    };
  });

  const origins = rows.map((row) => row.origin);
  const origin = origins.every((o) => o !== undefined && o.pv !== null && o.grid !== null)
    ? (origins as Origin[]).reduce<Origin>(
        (acc, o) => ({
          pv: acc.pv! + o.pv!,
          grid: acc.grid! + o.grid!,
          coverage: Math.min(acc.coverage, o.coverage),
          quality: lessCertainQuality(acc.quality, o.quality),
        }),
        { pv: 0, grid: 0, coverage: 1, quality: "measured" },
      )
    : undefined;

  return {
    rows,
    sum,
    coverage: Math.min(...totals.map((total) => total?.coverage ?? 0)),
    quality: totals.reduce<Total["quality"]>(
      (acc, total) => (total === undefined ? acc : lessCertainQuality(acc, total.quality)),
      "measured",
    ),
    origin,
    stackable,
  };
}
